export class OptionCalculator {
  
  /**
   * Standard normal cumulative distribution function
   */
  private normalCDF(x: number): number {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x) / Math.sqrt(2.0);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return 0.5 * (1.0 + sign * y);
  }

  /**
   * Black-Scholes option pricing formula
   * @param S - Current stock price
   * @param K - Strike price
   * @param T - Time to expiration (in years)
   * @param r - Risk-free interest rate (annual)
   * @param sigma - Volatility (annual)
   * @param optionType - 'call' or 'put'
   */
  blackScholes(S: number, K: number, T: number, r: number, sigma: number, optionType: 'call' | 'put'): number {
    if (T <= 0) {
      // Option has expired
      if (optionType === 'call') {
        return Math.max(S - K, 0);
      } else {
        return Math.max(K - S, 0);
      }
    }

    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    if (optionType === 'call') {
      return S * this.normalCDF(d1) - K * Math.exp(-r * T) * this.normalCDF(d2);
    } else {
      return K * Math.exp(-r * T) * this.normalCDF(-d2) - S * this.normalCDF(-d1);
    }
  }

  /**
   * Binomial option pricing model (American options)
   * @param S - Current stock price
   * @param K - Strike price
   * @param T - Time to expiration (in years)
   * @param r - Risk-free interest rate (annual)
   * @param sigma - Volatility (annual)
   * @param optionType - 'call' or 'put'
   * @param n - Number of time steps
   */
  binomial(S: number, K: number, T: number, r: number, sigma: number, optionType: 'call' | 'put', n: number = 100): number {
    if (T <= 0) {
      // Option has expired
      if (optionType === 'call') {
        return Math.max(S - K, 0);
      } else {
        return Math.max(K - S, 0);
      }
    }

    const dt = T / n;
    const u = Math.exp(sigma * Math.sqrt(dt));
    const d = 1 / u;
    const p = (Math.exp(r * dt) - d) / (u - d);

    // Initialize asset prices at maturity
    const assetPrices: number[] = [];
    for (let i = 0; i <= n; i++) {
      assetPrices[i] = S * Math.pow(u, n - i) * Math.pow(d, i);
    }

    // Initialize option values at maturity
    const optionValues: number[] = [];
    for (let i = 0; i <= n; i++) {
      if (optionType === 'call') {
        optionValues[i] = Math.max(assetPrices[i] - K, 0);
      } else {
        optionValues[i] = Math.max(K - assetPrices[i], 0);
      }
    }

    // Step backwards through the tree
    for (let step = n - 1; step >= 0; step--) {
      for (let i = 0; i <= step; i++) {
        // Calculate the current asset price
        const currentAssetPrice = S * Math.pow(u, step - i) * Math.pow(d, i);
        
        // Calculate the option value from continuation
        const continueValue = Math.exp(-r * dt) * (p * optionValues[i] + (1 - p) * optionValues[i + 1]);
        
        // Calculate the intrinsic value (early exercise value for American options)
        let intrinsicValue: number;
        if (optionType === 'call') {
          intrinsicValue = Math.max(currentAssetPrice - K, 0);
        } else {
          intrinsicValue = Math.max(K - currentAssetPrice, 0);
        }
        
        // For American options, take the maximum of continuation and exercise values
        optionValues[i] = Math.max(continueValue, intrinsicValue);
      }
    }

    return optionValues[0];
  }

  /**
   * Calculate option Greeks for Black-Scholes model
   */
  calculateGreeks(S: number, K: number, T: number, r: number, sigma: number, optionType: 'call' | 'put') {
    if (T <= 0) {
      return {
        delta: 0,
        gamma: 0,
        theta: 0,
        vega: 0,
        rho: 0
      };
    }

    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);
    
    const nd1 = this.normalCDF(d1);
    const nd2 = this.normalCDF(d2);
    const pdf_d1 = Math.exp(-0.5 * d1 * d1) / Math.sqrt(2 * Math.PI);

    let delta: number, theta: number, rho: number;

    if (optionType === 'call') {
      delta = nd1;
      theta = (-S * pdf_d1 * sigma / (2 * Math.sqrt(T)) - r * K * Math.exp(-r * T) * nd2) / 365;
      rho = K * T * Math.exp(-r * T) * nd2 / 100;
    } else {
      delta = nd1 - 1;
      theta = (-S * pdf_d1 * sigma / (2 * Math.sqrt(T)) + r * K * Math.exp(-r * T) * this.normalCDF(-d2)) / 365;
      rho = -K * T * Math.exp(-r * T) * this.normalCDF(-d2) / 100;
    }

    const gamma = pdf_d1 / (S * sigma * Math.sqrt(T));
    const vega = S * pdf_d1 * Math.sqrt(T) / 100;

    return {
      delta: delta,
      gamma: gamma,
      theta: theta,
      vega: vega,
      rho: rho
    };
  }

  /**
   * Calculate option Greeks for American options using numerical approximation
   */
  calculateGreeksNumerical(S: number, K: number, T: number, r: number, sigma: number, optionType: 'call' | 'put', n: number = 100) {
    if (T <= 0) {
      return {
        delta: 0,
        gamma: 0,
        theta: 0,
        vega: 0,
        rho: 0
      };
    }

    const h = S * 0.01; // Use 1% of stock price for better scaling
    const basePrice = this.binomial(S, K, T, r, sigma, optionType, n);

    // Delta: ∂V/∂S
    const priceUp = this.binomial(S + h, K, T, r, sigma, optionType, n);
    const priceDown = this.binomial(S - h, K, T, r, sigma, optionType, n);
    const delta = (priceUp - priceDown) / (2 * h);

    // Gamma: ∂²V/∂S²
    const gamma = (priceUp - 2 * basePrice + priceDown) / (h * h);

    // Theta: -∂V/∂T (negative because time decay)
    const thetaH = 1 / 365; // One day in years
    const priceTheta = T > thetaH ? this.binomial(S, K, T - thetaH, r, sigma, optionType, n) : 0;
    const theta = -(basePrice - priceTheta) / thetaH / 365; // Convert to per day

    // Vega: ∂V/∂σ
    const vegaH = 0.01; // 1% volatility change
    const priceVegaUp = this.binomial(S, K, T, r, sigma + vegaH, optionType, n);
    const vega = (priceVegaUp - basePrice) / (vegaH * 100); // Convert to per 1% vol change

    // Rho: ∂V/∂r
    const rhoH = 0.01; // 1% interest rate change
    const priceRhoUp = this.binomial(S, K, T, r + rhoH, sigma, optionType, n);
    const rho = (priceRhoUp - basePrice) / (rhoH * 100); // Convert to per 1% rate change

    return {
      delta: delta,
      gamma: gamma,
      theta: theta,
      vega: vega,
      rho: rho
    };
  }
}
